import { forwardRef, MouseEventHandler } from 'react';
import { Box, BoxProps, darken } from '@rossum/ui/material';
import { CornerResizingDirection } from '../utils/resizing';

/**
 * This is a presentational component that only renders a circular corner handle
 * It needs to be used within a PageSpaceContext that provides transformational functions
 */
const cornerHandleProps = {
  'top-left': {
    style: {
      cursor: 'nwse-resize',
    },
  },
  'top-right': {
    cx: '100%',
    cy: 0,
    style: {
      cursor: 'nesw-resize',
    },
  },
  'bottom-left': {
    cx: 0,
    cy: '100%',
    style: {
      cursor: 'nesw-resize',
    },
  },
  'bottom-right': {
    cx: '100%',
    cy: '100%',
    style: {
      cursor: 'nwse-resize',
    },
  },
} as const;

type CornerHandleProps = Omit<BoxProps<'circle'>, 'ref'> & {
  direction: CornerResizingDirection;
  color?: string;
  strokeWidth: number;
  radius: number;
  onMouseDownCapture: MouseEventHandler<SVGCircleElement>;
};

export const CornerHandle = forwardRef<SVGCircleElement, CornerHandleProps>(
  ({ direction, color, strokeWidth, radius, onMouseDownCapture }, ref) => {
    return (
      <g>
        <Box
          component="circle"
          onMouseDownCapture={onMouseDownCapture}
          r={2 * radius}
          {...cornerHandleProps[direction]}
          style={{
            ...(cornerHandleProps[direction].style ?? {}),
          }}
          ref={ref}
          sx={{
            fill: 'transparent',
            pointerEvents: 'all',
            [`& ~ circle`]: {
              fill: color ?? (theme => theme.palette.primary.main),
              pointerEvents: 'none',
            },
            [`&:hover ~ circle`]: {
              // IBB: Fix colors
              fill: color
                ? darken(color, 0.5)
                : theme => theme.palette.primary.dark,
            },
          }}
        />
        <circle
          vectorEffect="non-scaling-stroke"
          r={radius}
          {...cornerHandleProps[direction]}
          strokeWidth={strokeWidth}
        />
      </g>
    );
  }
);
